import React, { useEffect, useState } from "react";
import { Video, Phone, CalendarClock, CheckCircle2, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/context/LanguageContext";

const C = {
  fr: { title: "Prendre rendez-vous", subtitle: "Réservez un échange en visioconférence ou par téléphone avec un conseiller. Confirmation par notre équipe.",
    type: "Type de rendez-vous", visio: "Visioconférence", phone: "Téléphone", datetime: "Date & heure souhaitées",
    message: "Message (optionnel)", messageP: "Précisez l'objet de votre rendez-vous…", request: "Réserver le rendez-vous",
    sending: "Envoi…", myAppts: "Mes rendez-vous", none: "Aucun rendez-vous.", wanted: "Prévu",
    confirmed: "Confirmé", join: "Rejoindre", chooseDate: "Choisissez une date souhaitée.",
    requestOk: "Demande de rendez-vous envoyée ! Notre équipe va la confirmer très prochainement.",
    stRequested: "En attente de confirmation", stConfirmed: "Confirmé", stDone: "Terminé", stCancelled: "Annulé" },
  en: { title: "Book an appointment", subtitle: "Book a video or phone meeting with an advisor. Confirmed by our team.",
    type: "Appointment type", visio: "Video call", phone: "Phone", datetime: "Preferred date & time",
    message: "Message (optional)", messageP: "Specify the purpose of your appointment…", request: "Book the appointment",
    sending: "Sending…", myAppts: "My appointments", none: "No appointment.", wanted: "Scheduled",
    confirmed: "Confirmed", join: "Join", chooseDate: "Please choose a preferred date.",
    requestOk: "Appointment request sent! Our team will confirm it shortly.",
    stRequested: "Awaiting confirmation", stConfirmed: "Confirmed", stDone: "Completed", stCancelled: "Cancelled" },
};

export default function RendezVous() {
  const { lang } = useLang();
  const c = C[lang];
  const locale = lang === "fr" ? "fr-FR" : "en-GB";
  const STATUS = {
    pending: { label: c.stRequested, cls: "bg-amber-50 text-amber-700", icon: Clock },
    requested: { label: c.stRequested, cls: "bg-amber-50 text-amber-700", icon: Clock },
    confirmed: { label: c.stConfirmed, cls: "bg-green-50 text-green-700", icon: CheckCircle2 },
    done: { label: c.stDone, cls: "bg-blue-50 text-blue-700", icon: CheckCircle2 },
    cancelled: { label: c.stCancelled, cls: "bg-red-50 text-red-700", icon: Clock },
  };
  const [appts, setAppts] = useState([]);
  const [form, setForm] = useState({ mode: "visio", preferred_date: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/appointments/me").then((r) => setAppts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.preferred_date) { toast.error(c.chooseDate); return; }
    setBusy(true);
    try {
      await api.post("/appointments", form);
      toast.success(c.requestOk);
      setForm({ mode: "visio", preferred_date: "", note: "" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="rendezvous-page">
      <h1 className="font-heading text-3xl sm:text-4xl text-navy">{c.title}</h1>
      <p className="text-muted-foreground mt-2">{c.subtitle}</p>

      <form onSubmit={submit} className="mt-8 bg-white border border-border p-6 space-y-5 max-w-2xl">
        <div>
          <Label className="text-navy">{c.type}</Label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {[{ v: "visio", icon: Video, l: c.visio }, { v: "telephone", icon: Phone, l: c.phone }].map((o) => (
              <button type="button" key={o.v} data-testid={`mode-${o.v}`} onClick={() => setForm({ ...form, mode: o.v })}
                className={`flex items-center gap-3 border p-4 transition-colors ${form.mode === o.v ? "border-gold bg-gold/5 text-navy" : "border-border text-muted-foreground hover:border-gold"}`}>
                <o.icon size={20} className={form.mode === o.v ? "text-gold" : ""} /> {o.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-navy">{c.datetime}</Label>
          <Input type="datetime-local" data-testid="appt-date" value={form.preferred_date}
            onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="mt-2 rounded-none" />
        </div>
        <div>
          <Label className="text-navy">{c.message}</Label>
          <Textarea data-testid="appt-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
            rows={3} className="mt-2 rounded-none" placeholder={c.messageP} />
        </div>
        <button type="submit" disabled={busy} data-testid="appt-submit" className="bg-gold hover:bg-gold-hover text-white px-6 py-3 font-medium transition-colors disabled:opacity-60 flex items-center gap-2">
          <CalendarClock size={18} /> {busy ? c.sending : c.request}
        </button>
      </form>

      <div className="mt-10">
        <h2 className="font-heading text-2xl text-navy">{c.myAppts}</h2>
        {appts.length === 0 ? (
          <div className="bg-white border border-border p-8 text-center mt-4 text-muted-foreground text-sm">{c.none}</div>
        ) : (
          <div className="space-y-3 mt-4">
            {appts.map((a) => {
              const st = STATUS[a.status] || STATUS.requested;
              return (
                <div key={a.id} data-testid={`appt-${a.id}`} className="bg-white border border-border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center">{a.mode === "visio" ? <Video size={18} /> : <Phone size={18} />}</div>
                    <div>
                      <p className="text-navy font-medium">{a.mode === "visio" ? c.visio : c.phone}</p>
                      <p className="text-xs text-muted-foreground">{c.wanted} : {new Date(a.preferred_date).toLocaleString(locale)}</p>
                      {a.confirmed_datetime && <p className="text-xs text-green-700 mt-0.5">{c.confirmed} : {new Date(a.confirmed_datetime).toLocaleString(locale)}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {a.meeting_link && a.status === "confirmed" && (
                      <a href={a.meeting_link} target="_blank" rel="noopener noreferrer" className="text-sm text-gold font-medium flex items-center gap-1 hover:underline">{c.join} <ExternalLink size={13} /></a>
                    )}
                    <span className={`flex items-center gap-1.5 text-xs px-3 py-1.5 ${st.cls}`}><st.icon size={13} /> {st.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
